import { Link, Navigate } from "react-router-dom";
import { ShieldCheck, Lock, Truck, Landmark } from "lucide-react";
import { useMember } from "@/context/MemberContext";
import { AtlLogo } from "@/components/store/Brand";
import { StoreFooter } from "@/components/store/StoreLayout";

const HERO = "https://static.prod-images.emergentagent.com/jobs/bcfa5605-bbd3-4de0-99e6-7e2f41372e25/images/6ac8b948f934b184537177e206d9f744195bc3436ba89b2c3cadf47912977745.jpeg";

const POINTS = [
  { icon: Lock, title: "Members only", text: "Products and prices are only visible to verified members." },
  { icon: ShieldCheck, title: "Verified accounts", text: "Every account is confirmed by email before it can shop." },
  { icon: Landmark, title: "Bank transfer", text: "Simple, secure payment with an automatic transfer discount." },
  { icon: Truck, title: "Flat-rate shipping", text: "One simple flat-rate Australian shipping fee on every order." },
];

export default function Landing() {
  const { member } = useMember();
  if (member) return <Navigate to="/shop" replace />;
  return (
    <div className="flex min-h-screen flex-col bg-white" data-testid="landing-page">
      <header className="absolute inset-x-0 top-0 z-20">
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-5 sm:px-8">
          <AtlLogo />
          <div className="flex items-center gap-2">
            <Link to="/login" data-testid="landing-header-login" className="rounded-lg px-4 py-2 text-sm font-semibold text-slate-800 transition-colors hover:bg-white/70">Log in</Link>
            <Link to="/register" data-testid="landing-header-register" className="hidden rounded-lg bg-slate-900 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-slate-700 sm:inline-block">Register</Link>
          </div>
        </div>
      </header>

      <section className="relative overflow-hidden bg-gradient-to-b from-sky-50 via-white to-white">
        <div className="mx-auto grid max-w-7xl items-center gap-12 px-5 pb-20 pt-32 sm:px-8 lg:grid-cols-[1.1fr_1fr] lg:pt-40">
          <div className="animate-[fadeUp_.5s_ease-out]">
            <span className="inline-flex items-center gap-2 rounded-full border border-sky-200 bg-white px-3 py-1 text-xs font-semibold uppercase tracking-wider text-sky-800" data-testid="landing-private-badge">
              <Lock className="h-3.5 w-3.5" /> Private members store
            </span>
            <h1 className="mt-6 font-head text-4xl font-extrabold leading-[1.05] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
              Wellness, reserved<br />for <span className="relative inline-block"><span className="relative z-10">members.</span><span className="absolute inset-x-0 bottom-1 z-0 h-3 bg-[#89CFF0]/60 sm:h-4" /></span>
            </h1>
            <p className="mt-6 max-w-lg text-base leading-relaxed text-slate-600 md:text-lg">
              The ATL catalogue is private. Create a free account and verify your email to browse the range, see pricing and place orders.
            </p>
            <div className="mt-9 flex flex-col gap-3 sm:flex-row">
              <Link to="/register" data-testid="landing-register-btn" className="inline-flex h-12 items-center justify-center rounded-lg bg-[#89CFF0] px-7 font-semibold text-slate-900 transition-colors hover:bg-[#74C2EC]">Create an account</Link>
              <Link to="/login" data-testid="landing-login-btn" className="inline-flex h-12 items-center justify-center rounded-lg border border-slate-300 bg-white px-7 font-semibold text-slate-900 transition-colors hover:border-slate-900">I already have an account</Link>
            </div>
          </div>
          <div className="relative hidden lg:block">
            <div className="absolute -left-8 -top-8 h-40 w-40 rounded-full bg-[#89CFF0]/30 blur-2xl" />
            <div className="relative overflow-hidden rounded-3xl border border-slate-200 shadow-[0_30px_80px_rgba(15,23,42,0.12)]">
              <img src={HERO} alt="ATL vials" className="h-[480px] w-full object-cover object-center" />
              <div className="absolute inset-x-4 bottom-4 flex items-center gap-3 rounded-xl border border-white/60 bg-white/85 p-4 backdrop-blur-md">
                <ShieldCheck className="h-6 w-6 text-sky-600" />
                <div className="text-sm"><div className="font-semibold text-slate-900">Members-only access</div><div className="text-slate-600">Log in to view the full catalogue</div></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-slate-100 bg-white">
        <div className="mx-auto grid max-w-7xl gap-px overflow-hidden px-5 py-16 sm:grid-cols-2 sm:px-8 lg:grid-cols-4">
          {POINTS.map(({ icon: Icon, title, text }, i) => (
            <div key={title} className="p-6 animate-[fadeUp_.5s_ease-out_both]" style={{ animationDelay: `${120 + i * 80}ms` }}>
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-sky-50 text-sky-700"><Icon className="h-5 w-5" /></div>
              <h3 className="mt-4 font-head text-lg font-bold text-slate-900">{title}</h3>
              <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{text}</p>
            </div>
          ))}
        </div>
      </section>
      <StoreFooter />
    </div>
  );
}
